import { Timestamp } from 'firebase/firestore';

export type TimestampLike =
  | Timestamp
  | { seconds: number; nanoseconds: number }
  | { _seconds: number; _nanoseconds: number }
  | string
  | number
  | Date;

/**
 * Every timestamp shape this app actually sees, normalized to a Date: a Firestore Timestamp (from
 * onSnapshot), its REST/JSON form (`_seconds`), an ISO string, epoch millis, or a Date. Anything
 * unusable — blank string, missing seconds, unparseable value — comes back null rather than an
 * Invalid Date, so callers can branch instead of rendering "Invalid Date" or blanking a screen.
 */
export const toDate = (timestamp?: TimestampLike | null): Date | null => {
  if (!timestamp) return null;

  const valid = (date: Date) => (isNaN(date.getTime()) ? null : date);

  if (timestamp instanceof Date) return valid(timestamp);
  if (typeof timestamp === 'string' || typeof timestamp === 'number') return valid(new Date(timestamp));
  if (typeof (timestamp as Timestamp).toDate === 'function') return valid((timestamp as Timestamp).toDate());

  const seconds = '_seconds' in timestamp ? timestamp._seconds : timestamp.seconds;
  const nanoseconds = '_nanoseconds' in timestamp ? timestamp._nanoseconds : timestamp.nanoseconds;
  if (typeof seconds !== 'number' || !isFinite(seconds)) return null;

  const millis = seconds * 1000 + (typeof nanoseconds === 'number' ? Math.floor(nanoseconds / 1e6) : 0);
  return valid(new Date(millis));
};

export const formatFirebaseDate = (date: Timestamp | { seconds: number; nanoseconds: number } | { _seconds: number; _nanoseconds: number } | null | undefined) => {
    if (!date) return 'Invalid Date';

    try {
        if (date instanceof Timestamp) {
            return date.toDate().toLocaleDateString();
        }

        const seconds = '_seconds' in date ? date._seconds : date.seconds;
        const nanoseconds = '_nanoseconds' in date ? date._nanoseconds : date.nanoseconds;

        if (typeof seconds !== 'number' || typeof nanoseconds !== 'number') {
            return 'Invalid Date';
        }

        const timestamp = new Timestamp(seconds, nanoseconds);
        return timestamp.toDate().toLocaleDateString();
    } catch (error) {
        console.error('Error formatting Firebase date:', error);
        return 'Invalid Date';
    }
};

export const formatPostingDate = (dateString: string): string => {
    if (!dateString) return '';
    
    const date = convertPostingDateToDate(dateString);
    return date.toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
};

export const convertPostingDateToDate = (dateString: string): Date => {
    const [yearStr, monthStr, dayStr] = dateString.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr); // 1-12
    const day = Number(dayStr);

    // Fallback to native parsing if the string is malformed
    if (!year || !month || !day) {
        const fallback = new Date(dateString);
        fallback.setHours(0, 0, 0, 0);
        return fallback;
    }

    const date = new Date(year, month - 1, day);
    date.setHours(0, 0, 0, 0);
    return date;
};

export const formatDateToYYYYMMDD = (date: Date): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Generates an array of available dates for retail collabs between a start and end date
 * @param startDate The campaign start date
 * @param endDate The campaign end date (optional)
 * @param defaultDaysOffset Default number of days to add from start date if no end date (default: 90)
 * @returns Array of date strings in YYYY-MM-DD format
 */
export const generateAvailableDates = (
    startDate: Date,
    endDate?: Date | null,
    defaultDaysOffset: number = 90
): string[] => {
    // Get today's date at midnight UTC
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    
    // Use the later of startDate and today as the minimum date
    const minDate = startDate > today ? startDate : today;
    
    // Calculate final end date - use provided endDate or default offset from minDate
    const finalEndDate = endDate || new Date(minDate.getTime() + defaultDaysOffset * 24 * 60 * 60 * 1000);
    
    // Generate dates array
    const dates: string[] = [];
    let currentDate = new Date(minDate);
    
    while (currentDate <= finalEndDate) {
        // Ensure we get the correct date string regardless of timezone
        const formattedDate = formatDateToYYYYMMDD(currentDate);
        dates.push(formattedDate);
        currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
};

/**
 * Formats a date string (YYYY-MM-DD) to short format (e.g., "Oct 25")
 * @param dateString Date string in YYYY-MM-DD format
 * @returns Formatted date string (e.g., "Oct 25")
 */
export const formatDateShort = (date: Date | string) => {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

/**
 * Calculates the number of days between today and a target date
 * @param dateString Date string in YYYY-MM-DD format
 * @returns Number of days (positive if in future, negative if in past)
 */
export const calculateDaysUntil = (targetDate: Date): number => {
    if (!targetDate) return 0;
    
    const today = new Date();
    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
};
